'use client';

import React from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, Receipt } from 'lucide-react';
import {
  SMARTTASK_TOPMOST_FULLSCREEN_MODAL_Z_INDEX,
} from '../viewport';
import { CHILD_ENTRY_FORM_Z_INDEX } from './expense';
import type { ChildEntryType, FeatureFlags } from '../types';
import EntryTypeSelector from './EntryTypeSelector';

export interface TypeSelectionScreenProps {
  isMobile: boolean;
  isDark: boolean;
  procurementHiddenTypes: ChildEntryType[];
  features: FeatureFlags | null | undefined;
  onCancel: () => void;
  onBackToBeginAction?: () => void;
  onSelect: (type: ChildEntryType, isJobLinked?: boolean) => void;
}

export function TypeSelectionScreen({
  isMobile,
  isDark,
  procurementHiddenTypes,
  features,
  onCancel,
  onBackToBeginAction,
  onSelect,
}: TypeSelectionScreenProps) {
  const content = (
    <div
      className="fixed inset-0 flex flex-col bg-white dark:bg-gray-900"
      style={{
        paddingTop: isMobile ? 'env(safe-area-inset-top)' : undefined,
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom)' : undefined,
        zIndex: Math.max(SMARTTASK_TOPMOST_FULLSCREEN_MODAL_Z_INDEX, CHILD_ENTRY_FORM_Z_INDEX),
      }}
    >
      {/* ── Sticky Header ─────────────────────────────────────── */}
      <div className="shadow-sm flex-shrink-0 bg-white dark:bg-gray-800 transition-[padding] duration-200">
        <div className="max-w-4xl mx-auto px-3 py-3 relative">
          {onBackToBeginAction && (
            <button
              type="button"
              onClick={onBackToBeginAction}
              className="absolute left-3 top-3 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
              aria-label="Go back"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}

          <button
            onClick={onCancel}
            className="absolute right-3 top-3 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="text-center pt-1">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg sm:mb-3 sm:h-14 sm:w-14">
              <Receipt className="h-6 w-6 sm:h-7 sm:w-7" />
            </div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl">Select Entry Type</h1>
          </div>
        </div>
      </div>

      {/* Type Selection Menu */}
      <div className="flex-1 overflow-y-auto px-3 py-3">
        <EntryTypeSelector
          isDark={isDark}
          hiddenTypes={procurementHiddenTypes}
          features={features}
          onSelect={onSelect}
        />
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(content, document.body)
    : content;
}
